import {Injectable} from '@angular/core';
import {concatMap, EMPTY, first, firstValueFrom, map, Observable, Subject, Subscription} from 'rxjs';
import {BackendService} from './backend.service';
import {InfoHubFavorites} from './DataModels/InfoHubFavorites';
import {FoodLog, FoodRecord} from './DataModels/FoodRecord';
import {NotificationSettings} from './DataModels/NotificationSettings';
import {Recipe} from './DataModels/Recipe';
import {TrackedDay} from './DataModels/TrackedDay';
import {IUser, TypicalMeal, User, UserChangedEvent, UserChangeType} from './DataModels/User';
import {toJSONLocalArray} from './helpers/helpers';
import {Questionnaire} from './questionnaire/questionnaireClassesAndEnums';
import {StorageService} from './storage.service';
import {ILastTrackerUsed, LastTrackerUsed} from './DataModels/LastTrackerUsed';
import {ChangeItemLog, FoodHistory, isChangeItemLog} from './DataModels/FoodHistory';
import {UUID} from 'angular2-uuid';
import {TutorialsDone} from './DataModels/TutorialsDone';
import {ConnectivityService} from './connectivity.service';
import {catchError, tap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  public user: User;

  public lastTrackerUsed: LastTrackerUsed;

  public serviceReady = false;
  public serviceReadySubject: Subject<boolean> = new Subject<boolean>();
  public newUserAssignedSubject: Subject<boolean> = new Subject<boolean>();
  public userDeletionResultSubject: Subject<UserDeletionResult> = new Subject<UserDeletionResult>();
  public trackedDayUpdateSuccessSubject: Subject<boolean> = new Subject<boolean>();
  public recipeSuccessfullyCreatedSubject: Subject<string> = new Subject<string>();
  public recipeDeletedSubject: Subject<string> = new Subject<string>();
  public localUserWasSuccessfullyTurnedIntoBackendUserSubject: Subject<boolean> = new Subject<boolean>();

  private _backendLoaded = false;
  private _storageLoaded = false;
  private _backendUser: User;
  private _storageUser: User;

  private waitForEmailVerifiedSubject: Subject<boolean> = new Subject<boolean>();
  private storageReadySubject: Subject<boolean> = new Subject<boolean>();

  private userChangedSubscription: Subscription;

  private updateUserInStorageQueue = new Subject<{ user: User, changeId?: string }>();

  // Subject to make sure all user changes are handled sequentially
  private userChangeEventQueue = new Subject<UserChangedEvent>();

  constructor(private storage: StorageService, private backend: BackendService, private connectivityService: ConnectivityService) {
    this.userChangeEventQueue.pipe(
        concatMap(event => this.handleUserChangedEvent(event))
    ).subscribe();
    this.updateUserInStorageQueue.pipe(
        concatMap(({user, changeId}) => this.reallyPerformUserInStorageUpdate(user, changeId))
    ).subscribe();
    this.loadUserFromStorage();
    this.loadLastTrackerUsedFromStorage();
  }

  // This method is called after app-launch (login / auto-login) when the necessary user-info is there to load user-info from backend
  public setUserInfo(turnLocalUserIntoRealAccount: boolean = false): void {
    console.log('Set user info was called!');
    this._backendUser = undefined;
    if (this.connectivityService.appIsOnline) {
      this.loadUserFromBackend(turnLocalUserIntoRealAccount);
      return;
    }
    // in case app is offline
    this.serviceIsReady('backend');
  }

  public waitForEmailVerified(): Subject<boolean> {
    this.isEmailVerified();
    return this.waitForEmailVerifiedSubject;
  }

  public generateAndUpdateChangeId(): string {
    const changeId: string = UUID.UUID();
    this.user.lastChangeId = changeId;
    this.updateUserInStorage(this.user, changeId);
    return changeId;
  }

  public async isLocalUser(): Promise<boolean> {
    if (this.serviceReady) {
      return this.user.isLocalUser;
    }
    if (this._storageLoaded) {
      return this._storageUser ? this._storageUser.isLocalUser : false;
    }
    await firstValueFrom(this.storageReadySubject.pipe(first()));
    return this._storageUser.isLocalUser;
  }

  public async hasStorageUser(): Promise<boolean> {
    if (this._storageLoaded) {
      return !!this._storageUser;
    }
    await firstValueFrom(this.storageReadySubject.pipe(first()));
    return !!this._storageUser;
  }

  public createLocalUser(): void {
    this.assignUserInstance(new User());
  }

  // method which loads the latest change id of the current user from backend, to compare against the id of storage user
  private loadUserChangeIdFromBackend() {
    console.log('Backend Service starts to fetch last change id');
    this.backend.getUserChangeId()
        .subscribe({
          next: resp => {
            if (resp.status !== 200) {
              this.loadUserFromBackend();
              return;
            }
            const changeId = resp.body.response;
            console.log('Comparing change ids...');
            this.compareChangeIds(changeId);
          },
          error: err => {
            console.log(err);
            this.loadUserFromBackend();
          }
        });
  }

  private compareChangeIds(backendChangeId: string) {
    if (!this._storageUser || backendChangeId !== this._storageUser.lastChangeId) {
      console.log('Change Ids don\'t match, start loading user from backend and decide');
      this.loadUserFromBackend();
      return;
    }
    if (!this._storageUser.emailVerified) {
      console.log('Change Ids match, but mail is not verified yet! Load backend user anyway!');
      this.loadUserFromBackend();
      return;
    }
    console.log('Change Ids match! Not necessary to load user from backend.');
    this.serviceIsReady('backend');
  }

  // method which loads the current user from backend
  private loadUserFromBackend(turnLocalUserIntoRealAccount: boolean = false) {
    console.log('Backend Service starts to fetch user');
    this.backend.getUser()
        .subscribe({
          next: resp => {
            console.log('Backend Service received response...');
            console.log('Status: ' + resp.status);
            const iUser: IUser = {...resp.body};
            if (iUser) {
              console.log(iUser);
              let user = new User(iUser);
              if (turnLocalUserIntoRealAccount) {
                user = this.mergeLocalUserWithNewAccountUser(user);
              }
              this._backendUser = user;
            }
            this.serviceIsReady('backend');
          },
          error: err => {
            console.log(err);
            this.serviceIsReady('backend');
          }
        });
  }

  private mergeLocalUserWithNewAccountUser(user: User): User {
    console.log('A local user was turned into a real account and now gets merged!');
    console.log('Backend user:', user);
    console.log('Storage user:', this._storageUser);
    console.log('Current user:', this.user);
    // On the phone: storage user is not available, but this.user contains the current local user.
    // Taking it as basis, and merging the backend-relevant infos into it
    this.user.isLocalUser = false;
    this.user.id = user.id;
    this.user.userName = user.userName;
    this.user.claims = user.claims;
    this.user.lastChangeId = user.lastChangeId;
    this.user.lastChangedAt = user.lastChangedAt;
    this.user.email = user.email;
    if (!user.isInternalUser) {
      this.user.emailWasVerified();
    }
    this.user.identitiyProviderInfos = user.identitiyProviderInfos;
    console.log('Updating merged user in backend...');
    this.onUserChangedEvent(new UserChangedEvent(UserChangeType.merged_local_user_with_backend_account, this.user));
    return this.user;
  }

  // method which checks if the users email address is verified
  // the method calls itself every 5 seconds until the mail was verified
  private isEmailVerified() {
    this.backend.isEmailVerified()
        // resp is of type `HttpResponse<boolean>`
        .subscribe({
          next: async resp => {
            console.log('Status: ' + resp.status);
            // access the body directly, which is typed as `IUser`.
            const isVerified: boolean = resp.body;
            if (isVerified) {
              this.user.emailWasVerified();
              this.updateUserInStorage(this.user);
              this.waitForEmailVerifiedSubject.next(true);
            } else {
              setTimeout(() => {
                this.isEmailVerified();
              }, 5000);
            }
          },
          error: err => {
            console.log(err);
          }
        });
  }

  // method which loads the current user from storage
  private loadUserFromStorage() {
    // Fetch user
    const userPromise: Promise<IUser> = this.storage.getUser();
    console.log('Storage Service starts to fetch user');
    userPromise.then((val: IUser) => {
      console.log('Storage Service received response...');
      if (val) {
        console.log(val);
        this._storageUser = new User(val);
      } else {
        console.log('User-Promise returned but no value for user');
      }
      this.serviceIsReady('storage');
      this.storageReadySubject.next(true);
    });
  }

  private loadLastTrackerUsedFromStorage() {
    // Fetch last tracker used
    const promise: Promise<ILastTrackerUsed> = this.storage.getLastTrackerUsed();
    promise.then((val: ILastTrackerUsed) => {
      if (val) {
        this.lastTrackerUsed = new LastTrackerUsed(val);
      } else {
        this.lastTrackerUsed = new LastTrackerUsed();
      }
      this.lastTrackerUsed.changedSubject.subscribe(val => {
        if (val) {
          this.storage.storeLastTrackerUsed(this.lastTrackerUsed);
        }
      });
    });
  }

  // Method which is called after either backend or storage user was loaded and assigned to the user instance of this service
  private assignUserInstance(user: User) {
    // Set the user instance
    this.user = user;
    this.backend.setUser(user); // this does NOT send the user to the server. It just updates the user instance in backend service
    this.storage.storeUser(user).then();
    // Subscribe to the change events of this user instance, to always get notified if something relevant was changed.
    // Before, check if we already subscribed. If yes, unsubscribe to prevent duplicate subscriptions
    if (this.userChangedSubscription) {
      this.userChangedSubscription.unsubscribe();
    }
    this.userChangedSubscription = this.user.changedSubject.subscribe((event: UserChangedEvent) => {
      if (event) {
        this.onUserChangedEvent(event);
      }
    });
    this.newUserAssignedSubject.next(true);
    this.user.checkAutofill();
  }

  // Determine if storage or backend user should be taken as the main user
  private checkWhichUserIsMoreUpToDate(): void {
    console.log('Check which user is more up to date...');
    if (this._storageUser && !this._backendUser) {
      console.log('No backend user available. Taking frontend user.');
      this.assignUserInstance(this._storageUser);
      return;
    }
    if (!this._storageUser && this._backendUser) {
      console.log('No frontend user available. Taking backend user.');
      this.updateUserInStorage(this._backendUser, this._backendUser.lastChangeId);
      this.assignUserInstance(this._backendUser);
      return;
    }
    if (this._storageUser && this._backendUser) {
      if (this.checkIfUserChanged()) {
        console.log('New user logged in, taking backend user.');
        this.updateUserInStorage(this._backendUser, this._backendUser.lastChangeId);
        this.assignUserInstance(this._backendUser);
      } else if (this._storageUser.lastChangedAt.getTime() > this._backendUser.lastChangedAt.getTime()) {
        console.log('Storage user seems to be more up to date. Using it.');
        this.checkIfBackendMasterDataAreThere(this._backendUser);
        this.updateUserInBackend(this._storageUser, this._storageUser.lastChangeId).subscribe();
        this.assignUserInstance(this._storageUser);
      } else {
        console.log('Backend user seems to be more up to date. Using it.');
        this.updateUserInStorage(this._backendUser, this._backendUser.lastChangeId);
        this.assignUserInstance(this._backendUser);
      }
      return;
    }
  }

  // User could have changed after logout and re-login with new user
  private checkIfUserChanged(): boolean {
    if (!this._storageUser) {
      return true;
    }
    if (this._storageUser.id === null || this._storageUser.id === undefined) {
      return true;
    }
    return this._storageUser.id !== this._backendUser.id;
  }

  // Method which is called in case the backend user was loaded but less up to date than the local storage user.
  // In this case it needs to be checked if backend-relevant data like Id, username, createdAt and so on are there and
  // update them if necessary
  private checkIfBackendMasterDataAreThere(user: User) {
    if (!this._storageUser.userName) {
      this._storageUser.userName = user.userName;
    }
    if (!this._storageUser.id) {
      this._storageUser.id = user.id;
    }
    if (this._storageUser.isActive === undefined) {
      this._storageUser.isActive = user.isActive;
    }
    if (!this._storageUser.createdAt) {
      this._storageUser.createdAt = user.createdAt;
    }
    if (!this._storageUser.email) {
      this._storageUser.email = user.email;
    }
    if (this._storageUser.emailVerified === false && user.emailVerified) {
      this._storageUser.emailWasVerified();
    }
    if (!this._storageUser.identitiyProviderInfos) {
      this._storageUser.identitiyProviderInfos = user.identitiyProviderInfos;
    }
    if (!this._storageUser.claims && user.claims) {
      this._storageUser.claims = user.claims;
    }
    console.log('The user returned from backend was less up to date than the local storage user. Compared backend relevant data and inserted if necessary');
  }

  // This method is called after user was loaded from storage or backend
  // After both finished loading, the service is considered as ready
  private serviceIsReady(part: string) {
    console.log('The following part is ready: ' + part);
    if (part === 'storage') {
      this._storageLoaded = true;
      if (!this._storageUser || this._storageUser.isLocalUser) {
        if (!this._backendLoaded) {
          this.serviceIsReady('backend');
          return;
        }
      } else {
        this.loadUserChangeIdFromBackend();
      }
    }
    if (part === 'backend') {
      this._backendLoaded = true;
    }
    if (this._storageLoaded && this._backendLoaded) {
      this.checkWhichUserIsMoreUpToDate();
      console.log('UserService is ready! Following user is used:');
      console.log(this.user);
      this.serviceReady = true;
      this.serviceReadySubject.next(true);
      this.user?.foodHistory?.cleanUpFoodHistory();
    }
  }

  public DELETE_USER() {
    if (!this.user) {
      return;
    }
    this.deleteUserInStorage();
    this.deleteUserInBackend(this.user.id);
  }

  private deleteUserInBackend(userId: string) {
    this.backend.deleteUser(userId)
        // resp is of type `HttpResponse<string>`
        .subscribe({
          next: resp => {
            const statusCode: number = resp.status;
            if (statusCode === 204) {
              console.log('Deleted user successfully in backend');
              this.userDeletionResultSubject.next(UserDeletionResult.Success);
            } else {
              console.log('Something went wrong while deleting user in backend');
              this.userDeletionResultSubject.next(UserDeletionResult.Failed);
            }
          },
          error: err => {
            console.log(err);
            this.userDeletionResultSubject.next(UserDeletionResult.Failed);
          }
        });
  }

  private deleteUserInStorage() {
    this.storage.deleteUser();
  }

  // Method which is called after all persistence relevant changes on the user instance
  // It puts the changes in the queue, so that they are processed sequentially
  private onUserChangedEvent(event: UserChangedEvent): void {
    this.userChangeEventQueue.next(event);
  }

  // Method which is called by the queue, once it's the next event to be processed
  private handleUserChangedEvent(event: UserChangedEvent): Observable<void> {
    const changeId: string = !event.dontUpdateChangeId ? UUID.UUID() : this.user.lastChangeId;
    this.updateUserInStorage(this.user, changeId);
    if (this.user.isLocalUser || event.type === UserChangeType.only_storage_update) {
      this.sendSpecialSuccessEventsForLocalUser(event);
      return new Observable<void>(observer => observer.complete());
    }
    return new Observable<void>(observer => {
      console.log('Starting to process new backend update...');
      switch (event.type) {
        case UserChangeType.added_tracked_day:
          this.updateTrackedDayInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.changed_tracked_day:
          this.updateTrackedDayInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.changed_questionnaire:
          this.updateQuestionnaireInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.changed_typical_meals:
          this.updateTypicalMealsInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.changed_emissions_target:
          this.updateTypicalDailyEmissionsInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.added_used_day:
          this.updateDaysUsedInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.added_recipe:
          this.updateRecipeInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.changed_recipe:
          this.updateRecipeInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.deleted_recipe:
          this.deleteRecipeInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.updated_food_history:
          this.updateFoodHistoryInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.added_food_record:
          this.addFoodRecordInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.added_food_log:
          this.addFoodLogInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.changed_food_log:
          this.updateFoodLogInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.deleted_food_log:
          this.deleteFoodLogInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.changed_notificaton_settings:
          this.updateNotificationSettingsInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.changed_info_hub_favorites:
          this.updateInfoHubFavoritesInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.changed_tutorials_done:
          this.updateTutorialsDoneInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.autofill:
          this.updateTrackedDayInBackend(this.user.id, event.value, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.set_five_days_app_usage_feedback_shown:
          this.updateFiveDaysAppUsageFeedbackShownInBackend(this.user.id, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
        case UserChangeType.merged_local_user_with_backend_account:
          this.updateUserInBackend(event.value, changeId).subscribe({
            next: () => {
              observer.complete();
              this.localUserWasSuccessfullyTurnedIntoBackendUserSubject.next(true);
            }, error: () => observer.complete()});
          break;
        default:
          this.updateUserInBackend(this.user, changeId).subscribe({
            next: () => observer.complete(), error: () => observer.complete()});
          break;
      }
    });
  }

  // In some cases, so far when a recipe was created successfully and when a TrackedDay was updated successfully in the backend,
  // events are raised to notify other parts of the app about it. This is done by the backend method, which is not triggered
  // for local users. Therefore, these events need to be raised for local users independently. This is done by this method
  private sendSpecialSuccessEventsForLocalUser(event: UserChangedEvent): void {
    switch (event.type) {
      case UserChangeType.added_recipe:
        this.recipeSuccessfullyCreatedSubject.next((event.value as Recipe).id);
        return;
      case UserChangeType.deleted_recipe:
        this.recipeDeletedSubject.next(event.value as string);
      case UserChangeType.added_tracked_day:
      case UserChangeType.changed_tracked_day:
        this.trackedDayUpdateSuccessSubject.next(true);
        return;
    }
  }

  private updateUserInBackend(user: User, changeId: string = undefined): Observable<void> {
    if (!changeId) {
      changeId = UUID.UUID();
    }
    return this.backend.updateUser(user.toInterface(), changeId).pipe(
        tap(resp => {
            const iUser: IUser = {...resp.body};
            if (iUser) {
              console.log('Updated user successfully in backend');
            } else {
              console.log('Something went wrong while updating user in backend');
            }
          }),
        catchError(err => {
            console.log(err);
            return EMPTY;
          }),
        map(() => undefined)
    );
  }

  private updateTrackedDayInBackend(userId: string, trackedDay: TrackedDay, changeId: string): Observable<void> {
    if (trackedDay === null || trackedDay === undefined) {
      console.log('Tracked day could not be updated in the backend: no tracked day was provided!');
      this.trackedDayUpdateSuccessSubject.next(false);
      return new Observable<void>(observer => observer.complete());
    }
    if (!(trackedDay instanceof TrackedDay)) {
      console.log('Tracked day could not be updated in the backend: no tracked day was provided but something else:');
      console.log(trackedDay);
      this.trackedDayUpdateSuccessSubject.next(false);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateTrackedDay(userId, trackedDay.toInterface(), changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
            this.trackedDayUpdateSuccessSubject.next(true);
          } else {
            console.log('Something went wrong while updating user in backend');
            this.trackedDayUpdateSuccessSubject.next(false);
          }
        }),
        catchError(err => {
          console.log(err);
          this.trackedDayUpdateSuccessSubject.next(false);
          return EMPTY;
        }),
        map(() => undefined)
        );
  }

  private updateQuestionnaireInBackend(userId: string, questionnaire: Questionnaire, changeId: string): Observable<void> {
    if (questionnaire === null || questionnaire === undefined) {
      console.log('Questionnaire could not be updated in the backend: no questionnaire was provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (!(questionnaire instanceof Questionnaire)) {
      console.log('Questionnaire could not be updated in the backend: no questionnaire was provided but something else:');
      console.log(questionnaire);
      return new Observable<void>(observer => observer.complete());
    }
    this.backend.updateQuestionnaire(userId, questionnaire.toInterface(), changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateTypicalMealsInBackend(userId: string, typicalMeals: TypicalMeal[], changeId: string): Observable<void> {
    if (typicalMeals === null || typicalMeals === undefined) {
      console.log('Typical meals could not be updated in the backend: no typical meals were provided!');
      return new Observable<void>(observer => observer.complete());
    }
    // Missing: Typecheck for typical meals... not sure how to do this because TypicalMeal is an interface, not a class
    return this.backend.updateTypicalMeals(userId, typicalMeals, changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateTypicalDailyEmissionsInBackend(userId: string, typicalDailyEmissions: number, changeId: string): Observable<void> {
    if (typicalDailyEmissions === null || typicalDailyEmissions === undefined) {
      console.log('Typical daily emissions could not be updated in the backend: no typical daily emissions were provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (typeof typicalDailyEmissions !== 'number') {
      console.log('Typical daily emissions could not be updated in the backend: no typical daily emissions was provided ' +
          'but something else:');
      console.log(typicalDailyEmissions);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateTypicalDailyEmissions(userId, typicalDailyEmissions, changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateDaysUsedInBackend(userId: string, daysUsed: Date[], changeId: string): Observable<void> {
    if (daysUsed === null || daysUsed === undefined) {
      console.log('Days used could not be updated in the backend: no days used were provided!');
      return new Observable<void>(observer => observer.complete());
    }
    // CHeck if the first element of the array is really a date
    if (daysUsed.length > 0 && !(daysUsed[0] instanceof Date)) {
      console.log('Days used could not be updated in the backend: no Dates were provided but something else:');
      console.log(daysUsed);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateDaysUsed(userId, toJSONLocalArray(daysUsed), changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateRecipeInBackend(userId: string, recipe: Recipe, changeId: string): Observable<void> {
    if (recipe === null || recipe === undefined) {
      console.log('Recipe could not be updated in the backend: no recipe was provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (!(recipe instanceof Recipe)) {
      console.log('Recipe could not be updated in the backend: no recipe was provided but something else:');
      console.log(recipe);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateRecipe(userId, recipe.toInterface(), changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            // This is to give signal that a recipe was now created. in case an image should already be uploaded for that
            // recipe, this can now happen
            if (resp.body.response === 'Successfully added recipe') {
              this.recipeSuccessfullyCreatedSubject.next(recipe.id);
            }
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private deleteRecipeInBackend(userId: string, recipeId: string, changeId: string): Observable<void> {
    if (recipeId === null || recipeId === undefined) {
      console.log('Recipe could not be updated in the backend: no recipe id was provided!');
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.deleteRecipe(userId, recipeId, changeId).pipe(
        tap(resp => {
          const statusCode: number = resp.status;
          if (statusCode === 204) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateFoodHistoryInBackend(userId: string, foodHistory: FoodHistory, changeId: string): Observable<void> {
    if (foodHistory === null || foodHistory === undefined) {
      console.log('Food history could not be updated in the backend: no food history was provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (!(foodHistory instanceof FoodHistory)) {
      console.log('Food history could not be updated in the backend: no food history was provided but something else:');
      console.log(foodHistory);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateFoodHistory(userId, foodHistory.toInterface(), changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private addFoodRecordInBackend(userId: string, foodRecord: FoodRecord, changeId: string): Observable<void> {
    if (foodRecord === null || foodRecord === undefined) {
      console.log('Food record could not be updated in the backend: no food record was provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (!(foodRecord instanceof FoodRecord)) {
      console.log('Food record could not be updated in the backend: no food record was provided but something else:');
      console.log(foodRecord);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateFoodRecord(userId, foodRecord.toInterface(), changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private deleteFoodRecordInBackend(userId: string, recordId: string, changeId: string): Observable<void> {
    if (recordId === null || recordId === undefined) {
      console.log('Food record could not be deleted in the backend: no food record id was provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (typeof (recordId) !== 'string') {
      console.log('Food record could not be deleted in the backend: no food record id was provided but something else:');
      console.log(recordId);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.deleteFoodRecord(userId, recordId, changeId).pipe(
        tap(resp => {
          const statusCode: number = resp.status;
          if (statusCode === 204) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private addFoodLogInBackend(userId: string, foodLog: FoodLog, changeId: string): Observable<void> {
    if (foodLog === null || foodLog === undefined) {
      console.log('Food log could not be updated in the backend: no food log was provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (!(foodLog instanceof FoodLog)) {
      console.log('Food log could not be updated in the backend: no food log was provided but something else:');
      console.log(foodLog);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.addFoodLog(userId, foodLog.id, foodLog.toInterface(), changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateFoodLogInBackend(userId: string, foodLogs: ChangeItemLog, changeId: string): Observable<void> {
    if (foodLogs === null || foodLogs === undefined) {
      console.log('Food log could not be updated in the backend: no food log was provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (!(isChangeItemLog(foodLogs))) {
      console.log('Food log could not be updated in the backend: no food log was provided but something else:');
      console.log(foodLogs);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateFoodLog(userId, foodLogs.newFoodLog.id, foodLogs, changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private deleteFoodLogInBackend(userId: string, foodLog: FoodLog, changeId: string): Observable<void> {
    if (foodLog === null || foodLog === undefined) {
      console.log('Food log could not be deleted in the backend: no food log was provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (!(foodLog instanceof FoodLog)) {
      console.log('Food log could not be deleted in the backend: no food log was provided but something else:');
      console.log(foodLog);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.deleteFoodLog(userId, foodLog.id, foodLog.toInterface(), changeId).pipe(
        tap(resp => {
          const statusCode: number = resp.status;
          if (statusCode === 204) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateNotificationSettingsInBackend(userId: string, notificationSettings: NotificationSettings, changeId: string):
      Observable<void> {
    if (notificationSettings === null || notificationSettings === undefined) {
      console.log('Notification settings could not be updated in the backend: no notification settings were provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (!(notificationSettings instanceof NotificationSettings)) {
      console.log('Notification settings could not be updated in the backend: no notification settings were provided but something else:');
      console.log(notificationSettings);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateNotificationSettings(userId, notificationSettings.toInterface(), changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user notifications successfully in backend');
          } else {
            console.log('Something went wrong while updating user notifications in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateInfoHubFavoritesInBackend(userId: string, favorites: InfoHubFavorites, changeId: string): Observable<void> {
    if (favorites === null || favorites === undefined) {
      console.log('Info hub favorites could not be updated in the backend: no info hub favorites were provided!');
      return new Observable<void>(observer => observer.complete());
    }
    if (!(favorites instanceof InfoHubFavorites)) {
      console.log('Info hub favorites could not be updated in the backend: no info hub favorites were provided but something else:');
      console.log(favorites);
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateInfoHubFavorites(userId, favorites.toInterface(), changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateTutorialsDoneInBackend(userId: string, tutorialsDone: TutorialsDone, changeId: string): Observable<void> {
    if (tutorialsDone === null || tutorialsDone === undefined) {
      console.log('Tutorials done could not be updated in the backend: no tutorials done were provided!');
      return new Observable<void>(observer => observer.complete());
    }
    return this.backend.updateTutorialsDone(userId, tutorialsDone, changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateFiveDaysAppUsageFeedbackShownInBackend(userId: string, changeId: string): Observable<void> {
    return this.backend.updateFiveDaysAppUsageFeedbackShown(userId, changeId).pipe(
        tap(resp => {
          const str: string = resp.statusText;
          const statusCode: number = resp.status;
          if (str && statusCode === 201) {
            console.log('Updated user successfully in backend');
          } else {
            console.log('Something went wrong while updating user in backend');
          }
        }),
        catchError(err => {
          console.log(err);
          return EMPTY;
        }),
        map(() => undefined)
    );
  }

  private updateUserInStorage(user: User, changeId?: string): void {
    this.updateUserInStorageQueue.next({user, changeId});
  }

  // This method is only allowed to be called by the update queue, to ensure, that all the requests are performed sequentially
  private async reallyPerformUserInStorageUpdate(user: User, changeId?: string): Promise<void> {
    if (!changeId) {
      changeId = UUID.UUID();
    }
    await this.storage.storeUser(user, changeId);
  }

}

export enum UserDeletionResult {
  Success, Failed
}
